import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Dialog, Slide, IconButton } from '@mui/material';
import { useFormik } from "formik";
import * as Yup from "yup";
import useRequestsWOAccessToken from "../../utils/useRequestsWOAccessToken";
import Snackbars from "../../utils/Snackbars";
import CloseIcon from '@mui/icons-material/Close';
import mobile from '../../assets/images/mobile-alt.svg';
import passwordimg from '../../assets/images/passwordimg.svg';
import LogoIcon from "../../assets/images/LogoIcon.svg";
import LogoText from "../../assets/images/LogoText.svg";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { loadingToggleAction, loginAction } from "../../store/actions/AuthActions";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LoginModal = ({ open, onClose, onLoginSuccess }) => {
  const [step, setStep] = useState(1); // 1: Phone input, 2: OTP verification
  const { postRequestWOAccessToken } = useRequestsWOAccessToken();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [AlertData, setAlertData] = useState({
    variant: "",
    message: "",
  });
  
  const resetAlertData = () => {
    setTimeout(() => {
      setAlertData({
        variant: "",
        message: "",
      });
    }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
  };

  // Phone number form
  const phoneValidation = useFormik({
    initialValues: { phone: "" },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit mobile number!')
        .required('Mobile number is required'),
    }),
    onSubmit: async (values) => {
      try {
        let submitBtn = document.getElementById('submit-phone');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Processing...';

        const jsonpar = JSON.stringify({
          email: values.phone,
        });
        
        const res = await postRequestWOAccessToken("auth/login", jsonpar);
        
        if (res && res.code === 200) {
          setStep(2);
          otpValidation.setFieldValue("phone", values.phone);
          setAlertData({
            variant: "success",
            message: "OTP has been sent to your mobile number successfully."
          });
        } else {
          setAlertData({
            variant: "error",
            message: res.message || "Failed to send OTP"
          });
        }
      } catch (error) {
        setAlertData({
          variant: "error",
          message: "Something went wrong!"
        });
      } finally {
        resetAlertData();

        let submitBtn = document.getElementById('submit-phone');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
      }
    }
  });

  // OTP verification form
  const otpValidation = useFormik({
    initialValues: { 
      phone: "",
      phone_otp: "" 
    },
    validationSchema: Yup.object({
      phone: Yup.string().required(),
      phone_otp: Yup.string().required("OTP is required"),
    }),
    onSubmit: async (values) => {
      try {
        let submitBtn = document.getElementById('submit-otp');
        submitBtn.disabled = true;
        submitBtn.textContent = 'Processing...';

        const jsonpar = JSON.stringify({
          phone: values.phone,
          phone_otp: values.phone_otp,
        });
        
        const res = await postRequestWOAccessToken("auth/verify-phone", jsonpar);
        
        if (res && res.code === 200) {
          let tokenDetails = {
            guestMode: false,
            email: res.data.email,
            phone: res.data.phone,
            displayName: `${res.data.first_name} ${res.data.last_name}`,
            idToken: res.data.accesstoken,
            registered: true,
            refreshToken: res.data.refreshtoken,
            expiresIn: 3600,
          };
          const currentPath = window.location.pathname;
          const params = location.search ? location.search : "";
          dispatch(loadingToggleAction(true));
          dispatch(
            loginAction(tokenDetails, () => navigate(currentPath + params), {
              code: 200,
              status: "success",
              message: "Login successful !",
            })
          );
          
          if (onLoginSuccess) onLoginSuccess();
        } else {
          setAlertData({
            variant: "error",
            message: res.message || "Invalid OTP"
          });
        }
      } catch (error) {
        setAlertData({
          variant: "error",
          message: "Something went wrong!"
        });
      } finally {
        resetAlertData();
        
        let submitBtn = document.getElementById('submit-otp');
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit';
      }
    }
  });

  const handleResendOTP = async () => {
    try {
      let resendBtn = document.getElementById('resend-otp');
      resendBtn.disabled = true;
      resendBtn.classList.add("disabled-button");

      const jsonpar = JSON.stringify({
        phone: otpValidation.values.phone
      });
      
      const response = await postRequestWOAccessToken(`auth/resend-register-verification-otp-phone`, jsonpar);
      
      if (response.code === 200) {
        setAlertData({
          variant: "success",
          message: response.message || "OTP resent successfully"
        });
      } else {
        setAlertData({
          variant: "error",
          message: response.message || "Failed to resend OTP"
        });
      }
      
    } catch (error) {
      setAlertData({ 
        variant: "error", 
        message: "Something went wrong!" 
      });
    } finally {
      resetAlertData();

      let resendBtn = document.getElementById('resend-otp');
      resendBtn.disabled = false;
      resendBtn.classList.remove("disabled-button");
    }
  };

  // Reset step when modal is closed
  useEffect(() => {
    if (!open) {
      setStep(1);
      phoneValidation.resetForm();
      otpValidation.resetForm();
    }
  }, [open]);

  // Empty function to prevent modal from closing
  const handleClose = () => {
    // Do nothing - this prevents the modal from closing
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      disableEscapeKeyDown={true}
      hideBackdrop={false}
      PaperProps={{
        style: {
          position: 'fixed',
          bottom: 0,
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          height: '75%',
        },
      }}
    >
      <Box sx={{ p: 3, position: 'relative' }}>
        {/* <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton> */}
        
        {AlertData.message && <Snackbars variant={AlertData.variant} message={AlertData.message} />}
        
        {/* Logo Section */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <img src={LogoIcon} alt="Logo" width={80} style={{ marginBottom: "12px" }} />
          <img src={LogoText} alt="Logo" width={160} />
        </Box>
        
        <Typography variant="h4" component="h1" gutterBottom className="signin-title" sx={{ textAlign: 'center', mt: 2 }}>
          {step === 1 ? 'Sign In' : 'Verify OTP'}
        </Typography>
        
        {step === 1 ? (
          <form className="loginForm" onSubmit={phoneValidation.handleSubmit}>
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, mb: 2, color: 'text.secondary' }}>
              If you don't have an account yet, we'll create one for you
            </Typography>

            <div className="position-relative">
              <img src={mobile} alt="Mobile" width={24} className="textboxLefticon" />
              <TextField 
                variant="outlined" 
                fullWidth 
                margin="normal" 
                placeholder="Mobile Number" 
                autoComplete="off"
                name="phone"
                type="number"
                className={`form-control ${phoneValidation.touched.phone && phoneValidation.errors.phone ? 'is-invalid' : ''}`}
                value={phoneValidation.values.phone || ""}
                onChange={phoneValidation.handleChange}
                onBlur={phoneValidation.handleBlur}
                error={phoneValidation.touched.phone && Boolean(phoneValidation.errors.phone)}
                helperText={phoneValidation.touched.phone && phoneValidation.errors.phone}
              />
            </div>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                id="submit-phone"
                className="btn-common btn bg-primary text-white btn-block"
                >
                Submit
                </Button>
            </Box>
          </form>
        ) : (
          <form className="loginForm" onSubmit={otpValidation.handleSubmit}>
            <div className="position-relative">
              <img src={mobile} alt="Mobile" width={24} className="textboxLefticon" />
              <TextField 
                variant="outlined" 
                fullWidth 
                margin="normal" 
                placeholder="Mobile Number" 
                disabled
                value={otpValidation.values.phone || ""}
              />
            </div>
            
            <div className="position-relative">
              <img src={passwordimg} alt="OTP" width={24} className="textboxLefticon" />
              <TextField 
                variant="outlined" 
                fullWidth 
                margin="normal" 
                placeholder="Enter OTP" 
                autoComplete="off"
                name="phone_otp"
                type="text"
                className={`form-control ${otpValidation.touched.phone_otp && otpValidation.errors.phone_otp ? 'is-invalid' : ''}`}
                value={otpValidation.values.phone_otp || ""}
                onChange={otpValidation.handleChange}
                onBlur={otpValidation.handleBlur}
                error={otpValidation.touched.phone_otp && Boolean(otpValidation.errors.phone_otp)}
                helperText={otpValidation.touched.phone_otp && otpValidation.errors.phone_otp}
              />
            </div>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 3 }}>
                <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                id="submit-otp"
                className="btn-common btn bg-primary text-white btn-block"
                >
                Submit
                </Button>
            </Box>
            
            <Typography 
              className="Resend-code" 
              id="resend-otp"
              onClick={handleResendOTP}
              sx={{ cursor: 'pointer', textAlign: 'right', color: '#6638A2' }}
            >
              Didn't receive otp? Tap to resend
            </Typography>
          </form>
        )}
      </Box>
    </Dialog>
  );
};

export default LoginModal;